import type { FC } from "react"
import { useMemo } from "react"
import { useQuery } from "@rocicorp/zero/react"
import Fuse from "fuse.js"
import { IconCheckOutline18 as Check } from "nucleo-ui-outline-18"
import { FixedSizeList } from "react-window"

import { cn } from "@productlane/lib/src/cn"
import { CommandItem } from "@productlane/ui/src/components/internal/command"
import { queries } from "@productlane/zero/queries"

import type { SharedFilterItemsProps, WithQuery } from "./types"
import { getProjectIcon } from "@/lib/projects"
import { useThreadsFilterState } from "../threads-filter-state"

export const LinkedProjectsFilterItems: FC<
	SharedFilterItemsProps & WithQuery
> = ({ onSelect, query }) => {
	const { projectIds, addProject, removeProject } = useThreadsFilterState()
	const [projects] = useQuery(queries.projects())

	const filteredProjects = useMemo(() => {
		const uniqueProjects = Array.from(
			new Map(projects.map((p) => [p.linearProjectId ?? p.id, p])).values(),
		)
		if (!query)
			return uniqueProjects.sort(
				(a, b) =>
					new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime(),
			)

		const fuse = new Fuse(uniqueProjects, {
			keys: [
				{ name: "name", weight: 2 },
				{ name: "publicTitle", weight: 2 },
				{ name: "description", weight: 1 },
				{ name: "publicDescription", weight: 1 },
			],
			threshold: 0.4,
		})
		const results = fuse.search(query)
		return results.map((result) => result.item)
	}, [projects, query])

	return (
		<>
			{filteredProjects.length ? (
				<div className="h-64">
					<FixedSizeList
						height={256}
						width="100%"
						itemCount={filteredProjects.length}
						itemData={filteredProjects}
						itemKey={(index, data) => data[index]?.id ?? index}
						itemSize={36}
						overscanCount={5}
						className="!h-full"
					>
						{({ data, index, style }) => {
							const project = data[index]

							if (!project) return null

							const Icon = getProjectIcon(project.icon)

							return (
								<CommandItem
									value={project.id}
									onSelect={() => {
										if (projectIds.includes(project.id)) {
											removeProject(project.id)
										} else {
											addProject(project.id)
										}
										onSelect()
									}}
									key={project.id}
									style={style}
								>
									<div className="flex w-full items-center gap-2">
										<Icon
											className="h-4 w-4 shrink-0"
											style={{
												...(project.color ? { color: project.color } : {}),
											}}
										/>
										<span className="flex-1 truncate">{project.name}</span>
										<Check
											className={cn("h-4 w-4 shrink-0", {
												"opacity-100": projectIds.includes(project.id),
												"opacity-0": !projectIds.includes(project.id),
											})}
										/>
									</div>
								</CommandItem>
							)
						}}
					</FixedSizeList>
				</div>
			) : (
				<div className="py-4 text-center text-sm">
					You haven&apos;t linked any projects yet
				</div>
			)}
		</>
	)
}
